import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import './EngagementBuyers.scss';
import {
    ColumnConfig,
    columnTemplates,
    ColumnTemplateType,
} from './BuyersSelectionStage/ColumnTemplates/ColumnTemplate';
import BuyersSelectionStage from './BuyersSelectionStage/BuyersSelectionStage';
import { STAGE_ITEMS } from '../../AdminMarketplace/Constants/dropdownItems';
import { GetTimelineStages } from '../../../slices/marketplace/timeline/thunks/get-timeline-stages.thunk';
import { getProjectedTransitionDateUtc } from '../../../slices/marketplace/timeline/utils/timeline-dates';
import { getEarlierValueRanges } from '../../../slices/marketplace/timeline/utils/value-range-carry-forward';
import { useStateSelector } from '../../../store/selectors';
import {
    MarketplaceOpportunityStatus,
    MarketplaceOpportunityStatusList,
} from '../../../slices/marketplace/opportunity/models/opportunity-status';
import ClickableText from './BuyersSelectionStage/ColumnTemplates/ColumnElements/ClickableText';
import { EngagementBuyer } from '../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { Timeline } from '../../../slices/marketplace/timeline/models/timeline.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import {
    BuyerSelectionOptionsList,
    BuyerDocumentExecutionOptionsList,
    TimelineActionType,
    NdaTermSelectionOptionsList,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { LoiSubmissionStatus } from '../../../slices/marketplace/timeline/models/enums/loi-submission-status.enum';
import { deriveLoiStatus } from '../../../slices/marketplace/timeline/utils/derive-loi-status';
import PortalTooltip from '../../../components/PortalTooltip/PortalTooltip';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import BuyerProfilePreviewPopup from '../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import RejectionReasonDialog from '../../../components/RejectionReasonDialog/RejectionReasonDialog';
import RejectionReasonReviewDialog from '../../../components/RejectionReasonReviewDialog/RejectionReasonReviewDialog';
import createNotification from '../../../utils/createNotification';
import Helpers from '../../../utils/helper';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import {
    DocumentStatusExecutionStatus,
    DocumentStatusExecutionStatusOptions,
} from '../../../slices/marketplace/timeline/models/enums/document-execution-status.enum';
import { DataNdaTermsType } from '../../../slices/marketplace/timeline/models/enums/data-nda-term-type';
import { Row } from 'reactstrap';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { EngagementBuyerMeetingDisplayStatus } from '../../../slices/marketplace/timeline/models/engagement-buyer-meeting-summary.model';
import { DocumentDetailsContentType } from '../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { NdaTerms } from '../../../slices/marketplace/timeline/models/nda-term-model';
import { NdaTermSelectionType } from '../../../slices/marketplace/timeline/models/enums/nda-term-selection-type';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import EngagementBuyersVisibilityTable from './EngagementBuyersVisibilityTable/EngagementBuyersVisibilityTable';
import { MarketplaceBuyer } from '../../../slices/marketplace/buyer/models/buyer';
import { EngagementProcessStatus } from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';

interface EngagementBuyersProps {
    engagementTimeline: Timeline;
    currentParty: TimelineParty;
    fetchData(
        fetchBuyers: boolean,
        noLoader: boolean
    ): Promise<any> | Promise<void>;
    navigateToDocument(documentId: string): void;
    availableBuyers?: EngagementBuyer[];
    systemBuyers?: MarketplaceBuyer[];
}

// #941: sellers identify buyers by company name plus the admin-entered acronym,
// e.g. "Harmony Veterinary Group, HVG". Falls back to the company name alone when no
// acronym is set.
export const formatBuyerDisplayName = (
    companyName: string,
    buyerAcronym?: string
): string =>
    buyerAcronym && buyerAcronym.trim()
        ? `${companyName}, ${buyerAcronym.trim()}`
        : companyName;

const EngagementBuyers = (props: EngagementBuyersProps) => {
    const dispatch = useDispatch() as any;

    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);

    const timelineStages = useStateSelector(
        (state) => state.timelines.timelineStages
    );

    const [sortedBuyers, setSortedBuyers] = useState<EngagementBuyer[]>([]);
    const [selectedEngagedBuyer, setSelectedEngagedBuyer] =
        useState<EngagementBuyer>(null);
    const [rejectionReasonToShow, setRejectionReasonToShow] = useState<{
        reason: string;
        isBuyerFeedback: boolean;
    }>(null);
    const [buyerUnderReview, setBuyerUnderReview] =
        useState<EngagementBuyer>(null);
    const axios = useStateSelector((state) => state.core.axios);

    const isAdvisor =
        props.currentParty === TimelineParty.VetValue ||
        props.currentParty === TimelineParty.Broker;

    const releaseRejectionReason = (buyerId: number, leaveComment: string) => {
        axios
            .post(
                `/api/Marketplace/MarketplaceOpportunities/${props.engagementTimeline.id}/ReleaseRejectionReason`,
                { buyerId, leaveComment }
            )
            .then(async (response: any) => {
                if (response?.request?.status === 200) {
                    setBuyerUnderReview(null);
                    await props.fetchData(true, false);
                    const buyers = getSortedBuyersByEngaged();
                    setSortedBuyers(buyers);
                } else {
                    createNotification(
                        'An error occurred while posting the rejection reason.',
                        'error'
                    );
                }
            });
    };

    const filteredStages = Array.from(
        new Map(
            props.engagementTimeline.events
                .map((event) =>
                    timelineStages.find((stage) => stage.id === event.stage)
                )
                .filter(Boolean)
                .map((stage) => [stage.id, stage])
        ).values()
    );

    const isSFASEngagement = isSfas(props.engagementTimeline.levelOfService);

    const fetchStages = async () => {
        await dispatch(GetTimelineStages());
    };

    const initiate = async () => {
        await fetchStages();
    };

    useEffect(() => {
        initiate().then(() => {
            const buyers = getSortedBuyersByEngaged();
            setSortedBuyers(buyers);
        });
        // Silent refresh so state changed from other tabs/parties (e.g. meeting
        // approvals) is current whenever this tab is opened.
        props.fetchData(true, true);
    }, []);

    useEffect(() => {
        setSortedBuyers(getSortedBuyersByEngaged());
    }, [props.engagementTimeline, props.availableBuyers]);

    const openBuyerProfile = (buyerId: number) => {
        selectEngagedBuyer(buyerId);
        setIsBuyerProfilePopupVisible(true);
    };

    const selectEngagedBuyer = (buyerId: number) => {
        const selectedBuyer =
            props.engagementTimeline.engagementProcess.buyers.find(
                (f) => f.buyerId === buyerId
            ) ?? props.availableBuyers?.find((f) => f.buyerId === buyerId);
        setSelectedEngagedBuyer(selectedBuyer);
    };

    const closeBuyerProfile = () => {
        setIsBuyerProfilePopupVisible(false);
        setSelectedEngagedBuyer(null);
    };

    const getTableColumns = (
        stageRelatedEvents: TimelineEvent[],
        currentStageId: number
    ) => {
        let columns = [] as ColumnConfig[];
        let loiColumnsAdded = false;
        let meetingsColumnAdded = false;

        stageRelatedEvents.forEach((e) => {
            const isDocumentColumn = BuyerDocumentExecutionOptionsList.some(
                (option) => {
                    return option.value === e.linkedAction?.actionType;
                }
            );

            const isDataNdaColumn = NdaTermSelectionOptionsList.some(
                (option) => {
                    return option.value === e.linkedAction?.actionType;
                }
            );

            const isFinancialModelColumn =
                e.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerSelectionDetailedFinancialModel;

            const isValueRangeEvent =
                e.linkedAction?.actionType ===
                TimelineActionType.BuyerExpressionOfInterestWithRange;

            const isIdNdaColumn =
                e.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerSelectionIdNda;

            const isVVBuyerSelectionColumn =
                e.linkedAction?.actionType ===
                TimelineActionType.VetValueBuyerSelectionAndTeaserPosting;
            const isExpressionOfInterestEvent =
                e.linkedAction?.actionType ===
                TimelineActionType.BuyerExpressionOfInterest;

            const isLoiEvent =
                e.linkedAction?.actionType ===
                    TimelineActionType.BuyerLoiSubmission ||
                e.linkedAction?.actionType ===
                    TimelineActionType.VetValueLoiFormCreation ||
                e.linkedAction?.actionType ===
                    TimelineActionType.BuyerLoiFormReview;

            const isMeetingsEvent =
                e.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetings;

            if (isDocumentColumn) {
                let column = mapDocumentsColumn(e);
                if (column !== null) columns.push(column);
            } else if (isDataNdaColumn) {
                let column = mapDataNdaColumn(e);
                if (column !== null) columns.push(column);
                let pipColumn = mapPipColumn(e);
                if (pipColumn !== null) columns.push(pipColumn);
                if (isSFASEngagement) {
                    let dfmColumn = mapDfmColumn(e);
                    if (dfmColumn !== null) columns.push(dfmColumn);
                }
            } else if (isValueRangeEvent) {
                let column = mapValRangeColumn(e);
                if (column !== null) columns.push(column);
            } else if (isFinancialModelColumn && !isSFASEngagement) {
                let column = mapDfmColumn(e);
                if (column !== null) columns.push(column);
            } else if (isIdNdaColumn) {
                let column = mapIdNdaColumn(e);
                if (column !== null) columns.push(column);
                let sellerProfileColumn = mapSellerProfileColumn(e);
                if (sellerProfileColumn !== null)
                    columns.push(sellerProfileColumn);
            } else if (
                isSFASEngagement
                    ? isVVBuyerSelectionColumn
                    : isExpressionOfInterestEvent
            ) {
                let column = mapVVBuyerSelectionColumn(e);
                if (column !== null) columns.push(column);
            } else if (isLoiEvent && !loiColumnsAdded) {
                loiColumnsAdded = true;
                let valColumn = mapLoiValuationColumn(e);
                if (valColumn !== null) columns.push(valColumn);
                let statusCol = mapLoiStatusColumn(stageRelatedEvents);
                if (statusCol !== null) columns.push(statusCol);
            } else if (isMeetingsEvent && !meetingsColumnAdded) {
                meetingsColumnAdded = true;
                let column = mapMeetingsColumn(e);
                if (column !== null) columns.push(column);
            }
        });

        const statusColumn = addStatusColumn(
            stageRelatedEvents,
            currentStageId
        );
        columns.push(statusColumn);

        return columns;
    };

    const getTableConfig = (
        stageRelatedEvents: TimelineEvent[],
        currentStageId: number
    ) => {
        const columns = getTableColumns(stageRelatedEvents, currentStageId);
        const data = getTableData(stageRelatedEvents);

        return {
            columns: columns,
            data: data,
        };
    };

    const getTableData = (stageRelatedEvents: TimelineEvent[]) => {
        const stageRelatedActionIds = stageRelatedEvents.map(
            (x) => x.linkedAction?.id
        );
        const buyerSelectionActionId =
            stageRelatedEvents.find((event) =>
                BuyerSelectionOptionsList.some((option) => {
                    return option.value === event.linkedAction?.actionType;
                })
            )?.linkedAction.id ?? 0;

        const buyerStates = sortedBuyers?.map((buyer) => {
            const buyerActionState = buyer.actionState ?? [];

            const stageRelatedBuyerSelectionState = buyerActionState.find(
                (x) => x.actionId === buyerSelectionActionId
            );
            const isBuyerSelected =
                stageRelatedBuyerSelectionState != null &&
                stageRelatedBuyerSelectionState.buyerStatus ===
                    MarketplaceOpportunityStatus.Engaged;

            const stageRelatedActionStates = buyerActionState.filter((x) =>
                stageRelatedActionIds.includes(x.actionId)
            );
            const lastActionState =
                stageRelatedActionStates.sort(
                    (a, b) =>
                        new Date(b.dateCreated).getTime() -
                        new Date(a.dateCreated).getTime()
                )[0] || null;

            if (lastActionState || isAnyEventActive(stageRelatedEvents)) {
                return {
                    id: buyer.id,
                    isActive: buyer.isActive,
                    hasViewedSellerProfile: buyer.hasViewedSellerProfile,
                    buyerId: buyer.buyerId,
                    companyName: buyer.companyName,
                    isBuyerSelected: isBuyerSelected,
                    actionState: buyerActionState,
                    opportunityBuyerStatus: buyer.opportunityBuyerStatus,
                    loiValuationTotal: buyer.loiValuationTotal,
                    meetingSummary: buyer.meetingSummary,
                    leaveComment: buyer.leaveComment,
                    rejectionReason: buyer.rejectionReason,
                    isLeaveCommentReleased: buyer.isLeaveCommentReleased,
                };
            }

            return null;
        });
        return buyerStates.filter((x) => x != null);
    };

    const getDateRange = (
        startDate: Date,
        dayStart: number,
        dayEnd: number
    ): { startDate: Date; endDate: Date } => {
        // Day offsets are marketplace business days, and dayEnd is exclusive, so the last day of
        // the window is dayEnd - 1. Projecting with local setDate arithmetic instead put these
        // dates in a different day space than the deadlines shown elsewhere.
        return {
            startDate: getProjectedTransitionDateUtc(startDate, dayStart),
            endDate: getProjectedTransitionDateUtc(startDate, dayEnd - 1),
        };
    };

    const mapValRangeColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                readonly: false,
                id: event.name + '-' + event.id,
                text: event.name,
                tooltipContent: `This column remains blank until a buyer posts their indicative range of value for your practice(s) at which time the range will be visible. Once the event closes, buyers who did not submit an update keep showing their previously provided range.`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const actionId = event.linkedAction.id;
                    const buyerActionState = row.actionState?.find(
                        (x) => x.actionId === actionId
                    );
                    if (
                        buyerActionState != null &&
                        buyerActionState.minPrice &&
                        buyerActionState.maxPrice
                    ) {
                        return (
                            <div className="buyer-state-nf-wrapper">
                                {getValRangeFormat(
                                    buyerActionState.minPrice,
                                    buyerActionState.maxPrice
                                )}
                                <NotificationFlag
                                    eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${row.buyerId}.${NotificationFlagsCategories.BuyerStateCode}.${event.id}`}/>
                            </div>
                        );
                    }
                    // #943: while the event is open, no-submission cells stay blank; once it
                    // completes, carry the latest earlier range forward (assumed unchanged).
                    // Buyers rejected by the seller stay blank.
                    if (
                        event.status !== TimelineEventStatus.Completed ||
                        row.opportunityBuyerStatus ===
                            MarketplaceOpportunityStatus.RejectedBySeller
                    ) {
                        return '';
                    }
                    const earlierRanges = getEarlierValueRanges(
                        event,
                        props.engagementTimeline.events,
                        row.actionState
                    );
                    const carriedForward = earlierRanges[0];
                    if (carriedForward == null) {
                        return '';
                    }
                    // #943: an explicit info icon next to the value — hovering a bare value is
                    // not discoverable. The tooltip carries the headline plus the buyer's range
                    // history (oldest first) so sellers can follow the evolution across events.
                    const rangeHistory = [...earlierRanges].reverse();
                    const tooltipText = [
                        'No update submitted — showing initial range.',
                        ...rangeHistory.map((state) => {
                            const eventName =
                                props.engagementTimeline.events.find(
                                    (e) =>
                                        e.linkedAction?.id === state.actionId
                                )?.name;
                            return `${eventName}: ${getValRangeFormat(
                                state.minPrice,
                                state.maxPrice
                            )}`;
                        }),
                    ].join('<br />');
                    return (
                        <div className="buyer-state-nf-wrapper">
                            {getValRangeFormat(
                                carriedForward.minPrice,
                                carriedForward.maxPrice
                            )}
                            <InfoTooltip
                                idText={`val-range-carried-${event.id}-${row.buyerId}`}
                                text={tooltipText}
                                place="top"
                            />
                        </div>
                    );
                },
            };
        }

        return null;
    };

    const loiStatusLabel = (status: LoiSubmissionStatus): string => {
        switch (status) {
            case LoiSubmissionStatus.WaitingSubmission:
                return 'Waiting Submission';
            case LoiSubmissionStatus.Submitted:
                return 'Submitted';
            case LoiSubmissionStatus.AdminReview:
                return 'Admin Review';
            case LoiSubmissionStatus.SellerReview:
                return 'Seller Review';
            case LoiSubmissionStatus.Executed:
                return 'Executed';
            case LoiSubmissionStatus.Rejected:
                return 'Rejected';
            default:
                return '';
        }
    };

    const loiStatusClassName = (status: LoiSubmissionStatus): string => {
        switch (status) {
            case LoiSubmissionStatus.Executed:
                return 'text-success';
            case LoiSubmissionStatus.Rejected:
                return 'text-danger';
            case LoiSubmissionStatus.WaitingSubmission:
            case LoiSubmissionStatus.Submitted:
            case LoiSubmissionStatus.AdminReview:
            case LoiSubmissionStatus.SellerReview:
                return 'text-warning';
            default:
                return '';
        }
    };

    const mapLoiValuationColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                readonly: false,
                id: 'loi-valuation',
                text: 'LOI Valuation',
                tooltipContent: `This column remains blank until a buyer has submitted their offer at which time the total value of the offer will be shown.`,
                formatter: (_: number, row: EngagementBuyer) => {
                    if (
                        row.loiValuationTotal != null &&
                        row.loiValuationTotal > 0
                    ) {
                        return Helpers.currencyFormat(
                            row.loiValuationTotal,
                            false
                        );
                    }
                    return 'NA';
                },
            };
        }
        return null;
    };

    const mapLoiStatusColumn = (events: TimelineEvent[]): ColumnConfig => {
        const loiSubmissionEvent = events.find(
            (e) => e.linkedAction?.actionType === TimelineActionType.BuyerLoiSubmission
        );

        if (!loiSubmissionEvent) return null;

        return {
            ...columnTemplates.find(
                (template) => template.id === ColumnTemplateType.Readonly
            ),
            dataField: 'buyerId',
            readonly: false,
            id: 'loi-status',
            text: 'LOI Status',
            tooltipContent: `<p><span>Waiting Submission</span> — The LOI stage is active, but the buyer has not yet submitted their LOI (shown in yellow)</p><p><span>Submitted</span> — The buyer has submitted their LOI (shown in yellow)</p><p><span>Admin Review</span> — The admin is reviewing and completing the detailed LOI form, including the window when the buyer adds comments to it (shown in yellow)</p><p><span>Seller Review</span> — The seller is reviewing the presented offers (shown in yellow)</p><p><span>Executed</span> — The seller selected this offer and the LOI document has been fully executed (shown in green)</p><p><span>Rejected</span> — The buyer or seller has rejected the opportunity at any point during the LOI stage, or the seller selected another offer (shown in red)</p>`,
            formatter: (_: number, row: EngagementBuyer) => {
                const status = deriveLoiStatus(
                    row,
                    props.engagementTimeline.events,
                    props.engagementTimeline.engagementData
                );
                if (status == null) return '';
                const label = loiStatusLabel(status);
                const className = loiStatusClassName(status);
                return <span className={className}>{label}</span>;
            },
        };
    };

    const mapSellerProfileColumn = (event: TimelineEvent): ColumnConfig => {
        const mapWithNf = (text: string, buyerId: number) => (
            <div className="buyer-state-nf-wrapper">
                {text}{' '}
                {text ? (
                    <NotificationFlag
                        eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${buyerId}.${NotificationFlagsCategories.BuyerSellerProfileCode}`}/>
                ) : (
                    []
                )}
            </div>
        );

        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: `Seller Profile`,
                readonly: true,
                id: event.name + '-' + event.id + '-seller-profile',
                tooltipContent: `<p><span>NA</span> — The ID NDA action has not been completed yet; profile access has not been unlocked</p><p><span>Not Viewed</span> — The ID NDA action is complete, but the buyer has not yet viewed the seller profile</p><p><span>Viewed</span> — The buyer has viewed the seller profile</p>`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const actionId = event.linkedAction.id;
                    const buyerActionState = row.actionState?.find(
                        (x) => x.actionId === actionId
                    );
                    if (
                        buyerActionState?.status ===
                        TimelineActionStatus.Completed
                    ) {
                        if (row.hasViewedSellerProfile) {
                            return mapWithNf('Viewed', row.buyerId);
                        } else {
                            return mapWithNf('Not Viewed', row.buyerId);
                        }
                    }
                    return mapWithNf('NA', row.buyerId);
                },
            };
        }

        return null;
    };

    const mapPipColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            const isPip =
                props.engagementTimeline.levelOfService ===
                    LevelOfService.FullServiceBrokerage ||
                isSfas(props.engagementTimeline.levelOfService);
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: `Provide ${isPip ? 'PIP' : 'PIQ'} to Buyers`,
                readonly: true,
                id: event.name + '-' + event.id + '-pip',
                tooltipContent: `<p>(blank) — Buyer did not reach this action and was rejected earlier. Also shown when the Provide PIP/PIQ action does not exist or documents were not provided/approved</p><p><span>Offered</span> — Appears automatically after the Data NDA document is signed by the Seller</p><p><span>Provided</span> — Appears when the Buyer downloads any of the PIP/PIQ documents</p>`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const buyerStateWrapper = (status: string) =>
                        mapBuyerStateWithNf(status, row.buyerId, event.id);
                    const actionId = event.linkedAction.id;

                    const actionState = row.actionState?.find(
                        (buyerAction) => buyerAction.actionId === actionId
                    );
                    const { buyerTerms } = getTermsStateForExecutedNDA(
                        actionState?.documentDetailsId,
                        row.buyerId
                    );
                    const textColor = getTermsColor(buyerTerms);

                    const events = props.engagementTimeline.events;
                    const documents =
                        props.engagementTimeline.engagementData.documentsList;
                    const pipDocumnents = documents.filter(
                        (doc) =>
                            doc.contentType ===
                            DocumentDetailsContentType.PipOrPiq
                    );
                    const pipEvents = events.filter((x) =>
                        pipDocumnents.some(
                            (y) => y.id === x.linkedAction?.referenceItemId
                        )
                    );
                    const pipActionIds = pipEvents
                        .filter(
                            (x) => x.status === TimelineEventStatus.Completed
                        )
                        .map((x) => x.linkedAction?.id);

                    if (pipActionIds.length === 0) {
                        return DocumentStatusExecutionStatusOptions[
                            DocumentStatusExecutionStatus.NA
                        ];
                    }
                    const pipBuyerActionStates = row.actionState.filter((x) =>
                        pipActionIds.some((y) => y === x.actionId)
                    );

                    const dataNDABuyerActionState = row.actionState?.find(
                        (x) => x.actionId === actionId
                    );

                    let result =
                        DocumentStatusExecutionStatusOptions[
                            DocumentStatusExecutionStatus.NA
                        ];

                    if (!!dataNDABuyerActionState) {
                        const dataNdaCompletedAndEngaged =
                            dataNDABuyerActionState?.status ===
                                TimelineActionStatus.Completed &&
                            dataNDABuyerActionState?.buyerStatus ===
                                MarketplaceOpportunityStatus.Engaged;

                        if (dataNdaCompletedAndEngaged) {
                            result =
                                DocumentStatusExecutionStatusOptions[
                                    DocumentStatusExecutionStatus.Offered
                                ];
                        }

                        if (
                            pipBuyerActionStates.some(
                                (x) =>
                                    x.buyerStatus ===
                                    MarketplaceOpportunityStatus.Engaged
                            )
                        ) {
                            result = 'Provided';
                        }
                    }

                    return (
                        <span style={{ color: textColor }}>
                            {buyerStateWrapper(result)}
                        </span>
                    );
                },
            };
        }

        return null;
    };

    const mapBuyerStateWithNf = (
        text: string,
        buyerId: number,
        eventId: string
    ) => (
        <div className="buyer-state-nf-wrapper">
            {text}{' '}
            {text ? (
                <NotificationFlag
                    eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${buyerId}.${NotificationFlagsCategories.BuyerStateCode}.${eventId}`}
                />
            ) : (
                []
            )}
        </div>
    );

    const mapDfmColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: 'Provide DFM to Buyers',
                readonly: true,
                id: event.name + '-' + event.id + '-dfm',
                tooltipContent: `<p><span>Not Offered</span> — The DFM has not been made available to the buyer yet, or the buyer was rejected by the seller</p><p><span>Offered</span> — The DFM is available to the buyer but has not been accepted yet</p><p><span>Accepted</span> — The buyer has accessed the DFM document</p>`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const buyerStateWrapper = (status: string) =>
                        mapBuyerStateWithNf(status, row.buyerId, event.id);
                    const actionId = event.linkedAction.id;
                    const events = props.engagementTimeline.events;
                    const documents =
                        props.engagementTimeline.engagementData.documentsList;
                    const dfmDocumnet = documents.filter(
                        (doc) =>
                            doc.contentType ===
                            DocumentDetailsContentType.FinancialModel
                    );
                    const dfmEvents = events.filter((x) =>
                        dfmDocumnet.some(
                            (y) => y.id === x.linkedAction?.referenceItemId
                        )
                    );
                    const dfmActions = dfmEvents
                        .filter(
                            (x) => x.status === TimelineEventStatus.Completed
                        )
                        .map((x) => x.linkedAction?.id);

                    if (dfmActions.length === 0) {
                        return buyerStateWrapper('Not Offered');
                    }

                    const buyerActionStates = row.actionState.filter((x) =>
                        dfmActions.some((y) => y === x.actionId)
                    );

                    const buyerActionState = row.actionState?.find(
                        (x) => x.actionId === actionId
                    );

                    if (isSFASEngagement) {
                        const { buyerTerms } = getTermsStateForExecutedNDA(
                            buyerActionState?.documentDetailsId,
                            row.buyerId
                        );
                        const textColor = getTermsColor(buyerTerms);

                        let result = 'Not Offered';

                        if (!!buyerActionState) {
                            const dataNdaCompletedAndEngaged =
                                buyerActionState?.status ===
                                    TimelineActionStatus.Completed &&
                                buyerActionState?.buyerStatus ===
                                    MarketplaceOpportunityStatus.Engaged;

                            if (dataNdaCompletedAndEngaged) {
                                result =
                                    DocumentStatusExecutionStatusOptions[
                                        DocumentStatusExecutionStatus.Offered
                                    ];
                            }

                            if (
                                buyerActionStates.some(
                                    (x) =>
                                        x.buyerStatus ===
                                        MarketplaceOpportunityStatus.Engaged
                                )
                            ) {
                                result =
                                    DocumentStatusExecutionStatusOptions[
                                        DocumentStatusExecutionStatus.Accepted
                                    ];
                            }
                        }

                        return (
                            <span style={{ color: textColor }}>
                                {buyerStateWrapper(result)}
                            </span>
                        );
                    }

                    if (
                        !!!buyerActionState ||
                        buyerActionState?.buyerStatus ===
                            MarketplaceOpportunityStatus.RejectedBySeller
                    ) {
                        return buyerStateWrapper('Not Offered');
                    }

                    if (
                        buyerActionStates.some(
                            (x) =>
                                x.buyerStatus ===
                                MarketplaceOpportunityStatus.Engaged
                        )
                    ) {
                        return buyerStateWrapper(
                            DocumentStatusExecutionStatusOptions[
                                DocumentStatusExecutionStatus.Accepted
                            ]
                        );
                    }

                    return buyerStateWrapper(
                        DocumentStatusExecutionStatusOptions[
                            DocumentStatusExecutionStatus.Offered
                        ]
                    );
                },
            };
        }

        return null;
    };

    const getLatestActionState = (states: EngagementBuyerActionState[]) => {
        const result = states
            .slice()
            .sort((a, b) => (b.dateCreated > a.dateCreated ? 1 : -1))[0];

        return result;
    };

    const addStatusColumn = (
        stageRelatedEvents: TimelineEvent[],
        currentStageId: number
    ): ColumnConfig => {
        const stageRelatedLinkedActionsIds = stageRelatedEvents.map(
            (x) => x.linkedAction?.id
        );

        const valuationRangeActionIds = stageRelatedEvents
            .filter(
                (x) =>
                    x.linkedAction?.actionType ===
                    TimelineActionType.BuyerExpressionOfInterestWithRange
            )
            .map((x) => x.linkedAction.id);

        return {
            ...columnTemplates.find(
                (template) => template.id === ColumnTemplateType.Readonly
            ),
            dataField: 'status',
            text: 'Status',
            readonly: true,
            id: 'statusStageId' + '-' + currentStageId.toString(),
            tooltipContent: `<p>(blank) — No action state exists for this buyer in this stage yet</p><p><span>Awaiting Buyer Response</span> — Buyer must complete an action on their side</p><p><span>Awaiting Seller Response</span> — Seller must complete an action on their side</p><p><span>Engaged</span> — The buyer is actively participating and moving through the process. Also shown once the buyer has submitted their updated valuation range</p><p><span>Buyer Missed Deadline</span> — The buyer did not perform the action within the required timeframe</p><p><span>Rejected by Buyer</span> — The buyer decided not to proceed with this opportunity</p><p><span>Rejected by Seller</span> — The seller decided not to move forward with this buyer</p>`,
            formatter: (_: number, row: EngagementBuyer) => {
                const mapStatus = (
                    text: string,
                    rejectionReason?: string,
                    showReviewAction?: boolean
                ) => (
                    <div className="buyer-state-nf-wrapper">
                        {rejectionReason?.trim() ? (
                            <span
                                className="rejection-reason-link"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setRejectionReasonToShow({
                                        reason: rejectionReason,
                                        isBuyerFeedback:
                                            !!row.isLeaveCommentReleased &&
                                            !!row.leaveComment?.trim(),
                                    });
                                }}>
                                {text}
                            </span>
                        ) : (
                            text
                        )}
                        {showReviewAction && (
                            <span
                                className="rejection-reason-review-link"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setBuyerUnderReview(row);
                                }}>
                                Review
                            </span>
                        )}{' '}
                        <NotificationFlag
                            eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${row.buyerId}.${NotificationFlagsCategories.BuyerStatusCode}.${currentStageId}`}/>
                    </div>
                );
                const actionState = row?.actionState ?? [];

                const buyerStageRelatedActionStates = actionState.filter((x) =>
                    stageRelatedLinkedActionsIds.includes(x.actionId)
                );

                if (buyerStageRelatedActionStates.length > 0) {
                    const lastActionState = getLatestActionState(
                        buyerStageRelatedActionStates
                    );

                    // Special case for completed Buyer Expression of Interest event
                    // that has Awaiting Buyer Response status to show "New Opportunity"
                    // only for SFAS engagements
                    if (
                        lastActionState?.buyerStatus ===
                            MarketplaceOpportunityStatus.AwaitingBuyerResponse &&
                        lastActionState?.status ===
                            TimelineActionStatus.Completed
                    ) {
                        const linkedAction =
                            props.engagementTimeline.events.find(
                                (ev) =>
                                    ev.linkedAction &&
                                    ev.linkedAction.id ===
                                        lastActionState.actionId
                            )?.linkedAction;

                        if (
                            linkedAction &&
                            linkedAction.actionType ==
                                TimelineActionType.BuyerExpressionOfInterest
                        ) {
                            return mapStatus(
                                MarketplaceOpportunityStatusList.find(
                                    (x) =>
                                        x.id ===
                                        MarketplaceOpportunityStatus.NewOpportunity
                                )?.name
                            );
                        }
                    }
                    const hasSubmittedValuationRange =
                        buyerStageRelatedActionStates.some(
                            (x) =>
                                valuationRangeActionIds.includes(x.actionId) &&
                                x.buyerStatus ===
                                    MarketplaceOpportunityStatus.Engaged
                        );

                    if (
                        lastActionState?.buyerStatus ===
                            MarketplaceOpportunityStatus.AwaitingBuyerResponse &&
                        hasSubmittedValuationRange
                    ) {
                        return mapStatus(
                            MarketplaceOpportunityStatusList.find(
                                (x) =>
                                    x.id ===
                                    MarketplaceOpportunityStatus.Engaged
                            )?.name
                        );
                    }

                    const isRejectedStatus =
                        lastActionState?.buyerStatus ===
                            MarketplaceOpportunityStatus.RejectedByBuyer ||
                        lastActionState?.buyerStatus ===
                            MarketplaceOpportunityStatus.RejectedBySeller;

                    const releasedFeedback = row.isLeaveCommentReleased
                        ? row.leaveComment
                        : undefined;

                    return mapStatus(
                        MarketplaceOpportunityStatusList.find(
                            (x) => x.id === lastActionState?.buyerStatus
                        )?.name,
                        isRejectedStatus
                            ? releasedFeedback || row.rejectionReason
                            : undefined,
                        isAdvisor &&
                            isRejectedStatus &&
                            !!row.leaveComment?.trim() &&
                            !row.isLeaveCommentReleased
                    );
                } else if (actionState.length > 0) {
                    const lastActionState = getLatestActionState(actionState);
                    if (
                        lastActionState?.buyerStatus ===
                        MarketplaceOpportunityStatus.Engaged
                    ) {
                        return mapStatus(
                            MarketplaceOpportunityStatusList.find(
                                (x) => x.id === lastActionState?.buyerStatus
                            )?.name
                        );
                    }
                }
                return '';
            },
        };
    };

    const getValRangeFormat = (minValue: number, maxValue: number) => {
        const minValueFormatted = Helpers.GetCurrencyFormat(minValue, 0, false);
        const maxValueFormatted = Helpers.GetCurrencyFormat(maxValue, 0, false);
        return `${minValueFormatted} - ${maxValueFormatted}`;
    };

    const mapDataNdaColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: event.name,
                readonly: true,
                id: event.name + '-' + event.id,
                tooltipContent: `<p>This column shows where each buyer stands in the process of agreeing to and signing the Data NDA. The color of the text matches the NDA blinding level — Green means the buyer sees full names, Orange means some names are hidden.</p><p><span>In Negotiation</span> — Both sides are actively working out the terms</p><p><span>Not Executed</span> — Both parties have agreed on the NDA terms, but the document has not been signed yet</p><p><span>Unblinded</span> — The NDA has been signed</p><p><span>DVMs / MGMT Blinded</span> — The NDA has been signed</p><p><span>Staff Blinded</span> — The NDA has been signed</p><p><span>Fully Blinded</span> — The NDA has been signed</p><p><span>Rejected by Seller</span> — The seller chose not to proceed with this buyer</p><p><span>Rejected by Buyer</span> — The buyer chose not to move forward with this opportunity</p>`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const actionId = event.linkedAction.id;
                    const actionState = row.actionState?.find(
                        (buyerAction) => buyerAction.actionId === actionId
                    );

                    const { buyerTerms } = getTermsStateForExecutedNDA(
                        actionState?.documentDetailsId,
                        row.buyerId
                    );

                    if (
                        actionState?.status ===
                            TimelineActionStatus.Completed &&
                        buyerTerms.dateModified < actionState.dateModified
                    ) {
                        return getExecutedNDADocumentFormatter(
                            actionId,
                            actionState?.documentDetailsId,
                            row,
                            event.id
                        );
                    }
                    if (actionState) {
                        return getNotExecutedNDADocumentFormatter(
                            event.linkedAction?.actionType,
                            actionId,
                            event.id,
                            row
                        );
                    }
                    return '';
                },
            };
        }

        return null;
    };

    const mapDocumentsColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: event.name,
                readonly: true,
                id: event.name + '-' + event.id,
                formatter: (_: number, row: EngagementBuyer) => {
                    const actionId = event.linkedAction.id;
                    const buyerActionState = row.actionState?.find(
                        (x) => x.actionId === actionId
                    );
                    const buyerStateWrapper = (status: string) =>
                        mapBuyerStateWithNf(status, row.buyerId, event.id);

                    if (!!!buyerActionState) {
                        return buyerStateWrapper(
                            DocumentStatusExecutionStatusOptions[
                                DocumentStatusExecutionStatus.NA
                            ]
                        );
                    }

                    if (
                        buyerActionState?.status ===
                        TimelineActionStatus.Completed
                    ) {
                        const documentLink = (
                            <div
                                className="link-text buyer-state-nf-wrapper"
                                onClick={() => {
                                    props.navigateToDocument(
                                        buyerActionState?.documentDetailsId
                                    );
                                }}>
                                {
                                    DocumentStatusExecutionStatusOptions[
                                        DocumentStatusExecutionStatus.Executed
                                    ]
                                }
                                <NotificationFlag
                                    eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${row.buyerId}.${NotificationFlagsCategories.BuyerStateCode}.${event.id}`}/>
                            </div>
                        );
                        return documentLink;
                    }
                    return buyerStateWrapper(
                        DocumentStatusExecutionStatusOptions[
                            DocumentStatusExecutionStatus.Accepted
                        ]
                    );
                },
            };
        }

        return null;
    };

    const mapVVBuyerSelectionColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            const tooltipContent = isSFASEngagement
                ? `<p>(blank) — The anonymized teaser has not yet been posted to this buyer</p><p><span>Posted</span> — The anonymized teaser has been published and is visible to the buyer, but the buyer has not yet viewed it</p><p><span>Viewed</span> — The buyer has viewed the anonymized teaser</p>`
                : `All buyers which express interest in your practice(s) will appear on this table having "Viewed" your teaser and engaged in your process.`;
            return {
                ...columnTemplates.find((t) => t.type === 'readonly'),
                dataField: 'buyerId',
                text: 'Anonymized Teaser',
                readonly: true,
                id: event.name + '-' + event.id,
                tooltipContent,
                formatter: (cell: number, _: any) => {
                    if (!isSFASEngagement) return 'Viewed';

                    const visibilityState =
                        props.engagementTimeline.engagementProcess?.buyerVisibilityStates?.find(
                            (bvs) => bvs.buyerId == cell
                        );

                    if (visibilityState?.isTeaserViewed) return 'Viewed';
                    if (visibilityState?.isTeaserPosted) return 'Posted';
                    return '';
                },
            };
        }

        return null;
    };

    const mapIdNdaColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: 'Buyer Execution of ID NDA',
                readonly: true,
                id: event.name + '-' + event.id,
                tooltipContent: `<p><span>Not Offered</span> — The ID NDA document has not been offered to the buyer yet, or the buyer was rejected by the seller, or the action failed</p><p><span>Offered</span> — The ID NDA document has been offered to the buyer but not yet signed</p><p><span>Executed</span> — The ID NDA has been fully signed; the cell is a clickable link to the signed document</p>`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const buyerStateWrapper = (status: string) =>
                        mapBuyerStateWithNf(status, row.buyerId, event.id);
                    const actionId = event.linkedAction.id;
                    const buyerActionState = row.actionState?.find(
                        (x) => x.actionId === actionId
                    );
                    if (!!!buyerActionState) {
                        return 'Not Offered';
                    }

                    if (
                        buyerActionState?.status === TimelineActionStatus.Failed
                    ) {
                        return 'Not Offered';
                    }

                    if (
                        buyerActionState?.buyerStatus ===
                        MarketplaceOpportunityStatus.RejectedBySeller
                    ) {
                        return 'Not Offered';
                    }

                    if (
                        buyerActionState?.status ===
                        TimelineActionStatus.Completed
                    ) {
                        const documentLink = (
                            <div
                                className="link-text"
                                onClick={() => {
                                    props.navigateToDocument(
                                        buyerActionState?.documentDetailsId
                                    );
                                }}>
                                {buyerStateWrapper(
                                    DocumentStatusExecutionStatusOptions[
                                        DocumentStatusExecutionStatus.Executed
                                    ]
                                )}
                            </div>
                        );
                        return documentLink;
                    }
                    return buyerStateWrapper(
                        DocumentStatusExecutionStatusOptions[
                            DocumentStatusExecutionStatus.Offered
                        ]
                    );
                },
            };
        }

        return null;
    };

    const formatMeetingTimeBlock = (
        dateStart: string,
        dateEnd: string
    ): string => {
        const start = moment(dateStart);
        const end = moment(dateEnd);
        return `${start.format('ddd M/D/YYYY')}, ${start.format(
            'h:mm A'
        )} - ${end.format('h:mm A')}`;
    };

    const mapMeetingsColumn = (event: TimelineEvent): ColumnConfig => {
        if (event != null) {
            const statusLabels = {
                [EngagementBuyerMeetingDisplayStatus.Proposed]: 'Proposed',
                [EngagementBuyerMeetingDisplayStatus.Scheduled]: 'Scheduled',
                [EngagementBuyerMeetingDisplayStatus.Occurred]: 'Occurred',
            };
            return {
                ...columnTemplates.find(
                    (template) => template.id === ColumnTemplateType.Readonly
                ),
                dataField: 'buyerId',
                text: 'Meetings',
                readonly: true,
                id: event.name + '-' + event.id,
                tooltipContent: `<p><span>Blank</span> — No meeting activity with the buyer yet</p><p><span>Proposed</span> — A meeting proposal is awaiting an answer from either side</p><p><span>Scheduled</span> — A meeting is confirmed and upcoming or in progress; an info icon appears when an alternative proposal is awaiting an answer</p><p><span>Occurred</span> — The meeting has been completed</p><p><span>Cancelled</span> — The proposal was cancelled and no other meeting activity exists</p>`,
                formatter: (_: number, row: EngagementBuyer) => {
                    const summary = row.meetingSummary;
                    if (!summary) return '';
                    if (
                        summary.status ===
                        EngagementBuyerMeetingDisplayStatus.Cancelled
                    ) {
                        return 'Cancelled';
                    }
                    const cellText = `${
                        statusLabels[summary.status]
                    } – ${formatMeetingTimeBlock(
                        summary.dateStart,
                        summary.dateEnd
                    )}`;
                    if (!summary.alternativeProposal) return cellText;
                    return (
                        <span>
                            {cellText}{' '}
                            <InfoTooltip
                                text=""
                                idText={`meetings-alt-${event.id}-${row.buyerId}`}
                                html={`Alternative proposed – ${formatMeetingTimeBlock(
                                    summary.alternativeProposal.dateStart,
                                    summary.alternativeProposal.dateEnd
                                )}`}
                                place="top"
                            />
                        </span>
                    );
                },
            };
        }

        return null;
    };

    const getNdaMatchInfo = (buyerTerms: NdaTerms) => {
        if (buyerTerms) {
            const isDvmsBlinded =
                buyerTerms.nonSolicitationDvmNamesBlinded !== null;
            const isStaffBlinded =
                buyerTerms.nonSolicitationStaffNamesBlinded !== null;
            return { isDvmsBlinded, isStaffBlinded };
        }

        return { isDvmsBlinded: false, isStaffBlinded: false };
    };

    const getTermsLabel = (buyerTerms: NdaTerms): string => {
        const match = getNdaMatchInfo(buyerTerms);
        if (match) {
            if (match.isDvmsBlinded && match.isStaffBlinded) {
                return 'Fully Blinded';
            }
            if (match.isStaffBlinded) {
                return 'Staff Blinded';
            }
            if (match.isDvmsBlinded) {
                return 'DVMs / MGMT Blinded';
            }
            return 'Unblinded';
        }
        return '';
    };

    const getTermsColor = (buyerTerms: NdaTerms): string => {
        const match = getNdaMatchInfo(buyerTerms);
        if (match) {
            if (match.isDvmsBlinded || match.isStaffBlinded) {
                return 'orange';
            }
            return 'green';
        }
        return 'black';
    };

    const isLastDataNdaDocumentAvailable = (
        actionType: TimelineActionType,
        actionId: number,
        buyerId?: number
    ): boolean => {
        if (
            NdaTermSelectionOptionsList.some((option) => {
                return option.value === actionType;
            })
        ) {
            const event = props.engagementTimeline.events.find(
                (x) => x.linkedAction?.id === actionId
            );
            const ndaTermSelectionId = event.linkedAction.dataNdaTermId;
            const termsSelection =
                props.engagementTimeline.engagementData.ndaTermSelectionList.find(
                    (x) => x.id === ndaTermSelectionId
                );
            if (termsSelection) {
                const buyerSelection = termsSelection.ndaTermsList.find(
                    (x) => x.buyerId === buyerId
                );

                const buyerAgreedTerms = [
                    ...(buyerSelection?.agreedTermsToDocumentList ?? []),
                ];
                buyerAgreedTerms.sort((a, b) => {
                    return b.dateLocked > a.dateLocked ? 1 : -1;
                });
                const lastAgreedTerms = buyerAgreedTerms.find((f) => f);
                if (!!lastAgreedTerms?.documentDetailsId) {
                    return true;
                }
            }
        }

        return false;
    };

    const formatYears = (type: DataNdaTermsType) => {
        if (type === DataNdaTermsType.OneYear) return '1 Year';
        if (type === DataNdaTermsType.NA) return 'N/A';

        const string = type.toString();

        return `${string} Years`;
    };

    const getSignedDocumentDetailsId = (
        actionType: TimelineActionType,
        actionId: number,
        buyerId?: number
    ) => {
        if (
            NdaTermSelectionOptionsList.some((option) => {
                return option.value === actionType;
            })
        ) {
            const event = props.engagementTimeline.events.find(
                (x) => x.linkedAction?.id === actionId
            );
            const ndaTermSelectionId = event.linkedAction.dataNdaTermId;
            const termsSelection =
                props.engagementTimeline.engagementData.ndaTermSelectionList.find(
                    (x) => x.id === ndaTermSelectionId
                );
            if (termsSelection) {
                const ndaTerms = termsSelection.ndaTermsList;
                const buyerTerms = ndaTerms.find(
                    (x) =>
                        x.buyerId === buyerId &&
                        !!x.agreedTermsToDocumentList?.length
                );
                if (!buyerTerms) {
                    return '';
                }
                return buyerTerms?.agreedTermsToDocumentList.find((x) => x)
                    .documentDetailsId;
            }
        }
    };

    const getNotExecutedDataNDATermsElement = (buyerId?: number) => {
        const termsSelection =
            props.engagementTimeline.engagementData.ndaTermSelectionList.find(
                (x) => x.type === NdaTermSelectionType.DataNdaTerms
            );
        if (termsSelection) {
            const ndaTerms = termsSelection.ndaTermsList;
            const buyerTerms = ndaTerms.find(
                (x) =>
                    x.buyerId === buyerId &&
                    !!x?.agreedTermsToDocumentList?.length
            );
            if (!buyerTerms) {
                return <></>;
            }
            const buyerAgreedTerems = [...buyerTerms.agreedTermsToDocumentList];
            buyerAgreedTerems.sort((a, b) => {
                return b.dateLocked > a.dateLocked ? 1 : -1;
            });

            const sellerTerms = ndaTerms.find(
                (x) =>
                    x.id ==
                    buyerAgreedTerems?.find((f) => f)?.agreedSellerNdaTermsId
            );
            const match = getNdaMatchInfo(buyerTerms);

            return renderBuyerTerms(sellerTerms, match);
        }
    };

    const renderBuyerTerms = (
        sellerTerms: NdaTerms,
        match: { isDvmsBlinded: boolean; isStaffBlinded: boolean }
    ) => {
        return (
            <>
                <Row>
                    <label className="mt-1">
                        Non-Disclosure Period:{' '}
                        <span className="period-value">
                            {sellerTerms.nonDisclosurePeriod != null
                                ? formatYears(sellerTerms.nonDisclosurePeriod)
                                : 'NA'}
                        </span>
                    </label>
                </Row>
                <Row>
                    <label>Non-Solicitation Period</label>
                </Row>
                <Row>
                    <div className="non-solicitation-period">
                        <div className="sub-title">
                            <label className="mr-1 ml-1">
                                DVMs / Management
                            </label>
                            {match.isDvmsBlinded ? (
                                <label className="mr-1 ml-3">
                                    Names Blinded:{' '}
                                    <span className="period-value">
                                        {sellerTerms.nonSolicitationDvmNamesBlinded !=
                                        null
                                            ? formatYears(
                                                  sellerTerms.nonSolicitationDvmNamesBlinded
                                              )
                                            : 'NA'}
                                    </span>
                                </label>
                            ) : (
                                <label className="mr-1 ml-3">
                                    Names Visible:{' '}
                                    <span className="period-value">
                                        {sellerTerms.nonSolicitationDvmNamesVisible !=
                                        null
                                            ? formatYears(
                                                  sellerTerms.nonSolicitationDvmNamesVisible
                                              )
                                            : 'NA'}
                                    </span>
                                </label>
                            )}
                        </div>
                        <div className="sub-title">
                            <label className="mr-1 ml-1">Staff</label>
                            {match.isStaffBlinded ? (
                                <label className="mr-1 ml-3">
                                    Names Blinded:{' '}
                                    <span className="period-value">
                                        {sellerTerms.nonSolicitationStaffNamesBlinded !=
                                        null
                                            ? formatYears(
                                                  sellerTerms.nonSolicitationStaffNamesBlinded
                                              )
                                            : 'NA'}
                                    </span>
                                </label>
                            ) : (
                                <label className="mr-1 ml-3">
                                    Names Visible:{' '}
                                    <span className="period-value">
                                        {sellerTerms.nonSolicitationStaffNamesVisible !=
                                        null
                                            ? formatYears(
                                                  sellerTerms.nonSolicitationStaffNamesVisible
                                              )
                                            : 'NA'}
                                    </span>
                                </label>
                            )}
                        </div>
                    </div>
                </Row>
            </>
        );
    };

    const getNotExecutedNDADocumentFormatter = (
        actionType: TimelineActionType,
        actionId: number,
        eventId: string,
        buyer?: EngagementBuyer
    ) => {
        const isDocumentAvailable = isLastDataNdaDocumentAvailable(
            actionType,
            actionId,
            buyer?.buyerId
        );

        if (isDocumentAvailable) {
            const tooltipId = new Date().getTime();

            const terms = getNotExecutedDataNDATermsElement(buyer?.buyerId);
            return (
                <div>
                    <span
                        className="icon-with-tooltip buyer-state-nf-wrapper"
                        id={
                            tooltipId.toString() +
                            actionId.toString() +
                            buyer?.buyerId?.toString()
                        }
                        style={{ color: 'black' }}
                        onClick={() => {}}>
                        Not Executed{' '}
                        <NotificationFlag
                            eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${buyer?.buyerId}.${NotificationFlagsCategories.BuyerStateCode}.${eventId}`}/>
                    </span>
                    {NdaTermSelectionOptionsList.some((option) => {
                        return option.value === actionType;
                    }) &&
                        terms && (
                            <PortalTooltip
                                anchorId={
                                    tooltipId.toString() +
                                    actionId.toString() +
                                    buyer?.buyerId?.toString()
                                }
                                delayShow={0}
                                place="top"
                                content={terms}
                            />
                        )}
                </div>
            );
        }
        let status = 'In Negotiation';
        if (
            buyer?.opportunityBuyerStatus ===
            MarketplaceOpportunityStatus.RejectedBySeller
        ) {
            status = 'Rejected by Seller';
        }
        if (
            buyer?.opportunityBuyerStatus ===
            MarketplaceOpportunityStatus.RejectedByBuyer
        ) {
            status = 'Rejected by Buyer';
        }

        const isRejectedStatus =
            buyer?.opportunityBuyerStatus ===
                MarketplaceOpportunityStatus.RejectedBySeller ||
            buyer?.opportunityBuyerStatus ===
                MarketplaceOpportunityStatus.RejectedByBuyer;
        const releasedFeedback = buyer?.isLeaveCommentReleased
            ? buyer?.leaveComment
            : undefined;
        const rejectionReason = isRejectedStatus
            ? releasedFeedback || buyer?.rejectionReason
            : undefined;

        return (
            <div className="buyer-state-nf-wrapper">
                {rejectionReason?.trim() ? (
                    <span
                        className="rejection-reason-link"
                        onClick={(event) => {
                            event.stopPropagation();
                            setRejectionReasonToShow({
                                reason: rejectionReason,
                                isBuyerFeedback: !!releasedFeedback?.trim(),
                            });
                        }}>
                        {status}
                    </span>
                ) : (
                    status
                )}{' '}
                <NotificationFlag
                    eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${buyer.buyerId}.${NotificationFlagsCategories.BuyerStateCode}.${eventId}`}/>
            </div>
        );
    };

    const getTermsStateForExecutedNDA = (
        executedDocumentId: string,
        buyerId: number
    ): {
        buyerTerms: NdaTerms;
        sellerTerms: NdaTerms;
    } => {
        const termsSelection =
            props.engagementTimeline.engagementData.ndaTermSelectionList.find(
                (x) => x.type === NdaTermSelectionType.DataNdaTerms
            );
        if (termsSelection) {
            const ndaTerms = termsSelection.ndaTermsList;
            const buyerTerms = ndaTerms.find(
                (x) =>
                    x.buyerId === buyerId &&
                    !!x?.agreedTermsToDocumentList?.length
            );

            const buyerAgreedTerems =
                buyerTerms?.agreedTermsToDocumentList.find(
                    (f) => f.documentDetailsId === executedDocumentId
                );

            const sellerTerms = ndaTerms.find(
                (x) => x.id == buyerAgreedTerems?.agreedSellerNdaTermsId
            );

            return { buyerTerms: buyerTerms, sellerTerms: sellerTerms };
        }

        return { buyerTerms: null, sellerTerms: null };
    };

    const getExecutedNDADocumentFormatter = (
        actionId: number,
        executedDocumentId: string,
        buyer: EngagementBuyer,
        eventId: string
    ) => {
        const tooltipId = new Date().getTime();

        const { buyerTerms, sellerTerms } = getTermsStateForExecutedNDA(
            executedDocumentId,
            buyer.buyerId
        );
        const match = getNdaMatchInfo(buyerTerms);

        const terms = renderBuyerTerms(sellerTerms, match);
        const checkmarkColor = getTermsColor(buyerTerms);
        const ndaLabel = getTermsLabel(buyerTerms);
        const result = (
            <div>
                {ndaLabel && (
                    <span
                        className="icon-with-tooltip buyer-state-nf-wrapper"
                        id={
                            tooltipId.toString() +
                            actionId.toString() +
                            buyer.buyerId?.toString()
                        }
                        style={{ color: checkmarkColor }}
                        onClick={() => {
                            if (executedDocumentId) {
                                props.navigateToDocument(executedDocumentId);
                            }
                        }}>
                        {ndaLabel}
                        <NotificationFlag
                            eventCode={`${NotificationFlagsCategories.MpCode}.${props.engagementTimeline.id}.${NotificationFlagsCategories.BuyersCode}.${buyer.buyerId}.${NotificationFlagsCategories.BuyerStateCode}.${eventId}`}/>
                    </span>
                )}
                {terms && (
                    <PortalTooltip
                        anchorId={
                            tooltipId.toString() +
                            actionId.toString() +
                            buyer.buyerId?.toString()
                        }
                        delayShow={0}
                        place="top"
                        content={terms}
                    />
                )}
            </div>
        );

        return result;
    };

    const getSortedBuyersByEngaged = (
        sortOrder: 'asc' | 'desc' = 'desc'
    ): EngagementBuyer[] => {
        const timeline = props.engagementTimeline;

        let engagedBuyers =
            props.engagementTimeline.engagementProcess.buyers || [];
        if (!timeline?.events || (!isSFASEngagement && !engagedBuyers))
            return [];

        if (isSFASEngagement) {
            const notEngagedAvailableBuyers = props.availableBuyers.filter(
                (ab) =>
                    !timeline.engagementProcess.buyerVisibilityStates?.find(
                        (bvs) => bvs.buyerId === ab.buyerId
                    )?.isTeaserPosted &&
                    timeline.engagementProcess.buyers.every(
                        (eb) => eb.buyerId !== ab.buyerId
                    )
            );
            engagedBuyers = engagedBuyers.concat(notEngagedAvailableBuyers);
        }

        const linkedAction = timeline.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.BuyerExpressionOfInterest
        )?.linkedAction;

        if (!linkedAction && !isSFASEngagement) return [];

        const engagedBuyersExtraFields = engagedBuyers
            .map((buyer: EngagementBuyer) => {
                const matchedAction = !buyer.actionState
                    ? null
                    : buyer.actionState?.find(
                          (action) =>
                              action.actionId === linkedAction?.id &&
                              action.buyerStatus ===
                                  MarketplaceOpportunityStatus.Engaged
                      );

                return {
                    ...buyer,
                    latestEngagedDate: matchedAction
                        ? new Date(matchedAction.dateCreated).getTime()
                        : new Date(0),
                };
            })
            .filter(Boolean)
            .sort((a: any, b: any) =>
                sortOrder === 'asc'
                    ? a.latestEngagedDate - b.latestEngagedDate
                    : b.latestEngagedDate - a.latestEngagedDate
            );

        return engagedBuyersExtraFields;
    };

    const isAnyEventActive = (events: TimelineEvent[]) => {
        const result = events.some(
            (x) =>
                x.status === TimelineEventStatus.InProgress ||
                x.status === TimelineEventStatus.Completed ||
                x.status === TimelineEventStatus.Late
        );
        return result;
    };

    const canShowBuyerVisibilityTable = () => {
        if (
            (props.currentParty == TimelineParty.VetValue ||
                props.currentParty == TimelineParty.Broker) &&
            isSfas(props.engagementTimeline.levelOfService)
        ) {
            return true;
        }
        return false;
    };

    const isBuyerVisibilityTableLocked = () => {
        if (
            props.engagementTimeline.engagementProcess.status ===
                EngagementProcessStatus.Suspended ||
            props.engagementTimeline.engagementProcess.status ===
                EngagementProcessStatus.Completed ||
            props.engagementTimeline.engagementProcess.status ===
                EngagementProcessStatus.Terminated
        ) {
            return true;
        }
        if (
            !(
                (props.currentParty == TimelineParty.VetValue ||
                    props.currentParty == TimelineParty.Broker) &&
                isSfas(props.engagementTimeline.levelOfService)
            )
        )
            return true;

        const buyerSelectionEvent = props.engagementTimeline.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
        );

        return buyerSelectionEvent?.status === TimelineEventStatus.Completed;
    };

    const isTeaserPostingAvailable = () => {
        const buyerSelectionEvent = props.engagementTimeline.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
        );

        return (
            buyerSelectionEvent?.status === TimelineEventStatus.InProgress ||
            buyerSelectionEvent?.status === TimelineEventStatus.Late
        );
    };

    const wasTeaserPostingCompleted = () => {
        const buyerSelectionEvent = props.engagementTimeline.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
        );

        return buyerSelectionEvent?.status === TimelineEventStatus.Completed;
    };

    return (
        <div className="engagement-buyers">
            <div className="body_inner_content">
                <div className="buyer-selection-stages-wrapper">
                    <div className="buyer-selection-stages-container">
                        <BuyersSelectionStage
                            key="buyers-table"
                            title="Buyers"
                            color={'#FFFFFF'}
                            columns={[
                                {
                                    component: ClickableText,
                                    dataField: 'companyName',
                                    readonly: false,
                                    action: (
                                        _: string,
                                        row: EngagementBuyer
                                    ) => {
                                        openBuyerProfile(row.buyerId);
                                    },
                                    text: '',
                                    id: 'buyer-name-stage-column-id',
                                },
                            ]}
                            data={sortedBuyers.map(
                                (buyer: EngagementBuyer) => ({
                                    id: buyer.id,
                                    companyName: formatBuyerDisplayName(
                                        buyer.companyName,
                                        buyer.buyerAcronym
                                    ),
                                    buyerId: buyer.buyerId,
                                })
                            )}
                            hideHeaderContent={true}
                        />

                        {filteredStages.map((stage, index) => {
                            const stageEvents =
                                props.engagementTimeline.events.filter(
                                    (f) => f.stage === stage.id
                                );

                            const { startDate, endDate } =
                                stageEvents.length > 0
                                    ? getDateRange(
                                          props.engagementTimeline
                                              .engagementProcess.startDate,
                                          stageEvents[0].dayStart,
                                          stageEvents[stageEvents.length - 1]
                                              .dayEnd
                                      )
                                    : {
                                          startDate: new Date(),
                                          endDate: new Date(),
                                      };

                            const stageColor =
                                STAGE_ITEMS[index]?.color || '#f8f9fa';

                            const finalStageColor = isAnyEventActive(
                                stageEvents
                            )
                                ? stageColor
                                : '#f8f9fa';

                            const config = getTableConfig(
                                stageEvents,
                                stage.id
                            );
                            return (
                                <BuyersSelectionStage
                                    key={stage.id}
                                    title={stage.name}
                                    color={finalStageColor}
                                    columns={config.columns || []}
                                    data={config.data || []}
                                    startDate={startDate}
                                    endDate={endDate}
                                    hideHeaderContent={false}
                                />
                            );
                        })}
                    </div>
                </div>

                {canShowBuyerVisibilityTable() && (
                    <div className="buyer-visibility-table-wrapper mt-4">
                        <EngagementBuyersVisibilityTable
                            isLocked={isBuyerVisibilityTableLocked()}
                            engagementId={props.engagementTimeline.id}
                            buyers={props.systemBuyers || []}
                            buyersVisibilityStates={
                                props.engagementTimeline.engagementProcess
                                    .buyerVisibilityStates || []
                            }
                            refreshBuyersData={async () => {
                                await props.fetchData(true, false);
                                const buyers = getSortedBuyersByEngaged();
                                setSortedBuyers(buyers);
                            }}
                            isTeaserPostingAvailable={isTeaserPostingAvailable()}
                            wasTeaserPostingCompleted={wasTeaserPostingCompleted()}
                        />
                    </div>
                )}
            </div>
            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedEngagedBuyer.buyerId}
                    close={closeBuyerProfile}
                />
            ) : (
                <></>
            )}
            <RejectionReasonDialog
                isVisible={!!rejectionReasonToShow}
                reason={rejectionReasonToShow?.reason ?? ''}
                isBuyerFeedback={rejectionReasonToShow?.isBuyerFeedback}
                onClose={() => setRejectionReasonToShow(null)}
            />
            <RejectionReasonReviewDialog
                isVisible={!!buyerUnderReview}
                initialReason={buyerUnderReview?.leaveComment ?? ''}
                onCancel={() => setBuyerUnderReview(null)}
                onRelease={(leaveComment) =>
                    releaseRejectionReason(
                        buyerUnderReview.buyerId,
                        leaveComment
                    )
                }
            />
        </div>
    );
};

export default EngagementBuyers;
